// ============================================================
//  INDUSTRY CONFIG — Single source of truth for all industry-specific defaults.
//  Every file that previously hardcoded landscaping values imports from here.
// ============================================================

export type IndustryType =
  | 'landscaping' | 'general_contractor' | 'concrete'
  | 'electrical' | 'plumbing' | 'painting'
  | 'cleaning' | 'hvac' | 'roofing' | 'custom';

/** Un encabezado del desplegable de «interés» y lo que cuelga de él. */
export interface ServiceInterestGroup {
  /** Solo se enseña. No se guarda en ninguna parte. */
  label: string;
  options: string[];
}

export interface IndustryConfig {
  label: string;
  icon: string;
  pricebookCategories: string[];
  /** La lista PLANA, incluido 'Other' al final. Es la que dice si un valor
   *  guardado sigue siendo uno de los de la casa; para pintar el desplegable
   *  se usa `serviceInterestGroups`. */
  serviceInterests: string[];
  serviceInterestGroups: ServiceInterestGroup[];
  clientTags: string[];
  defaultWarrantyName: string;
  defaultWarrantyText: string;
  scopePromptRole: string;
  legalPromptRole: string;
  warrantyPromptRole: string;
  chatSystemPrompt: string;
  setupPlaceholder: string;
  proposalLabel: string;
  defaultDescription: string;
  defaultUpsells: { id: string; name: string; price: number; description: string }[];
  showSiteConditions: boolean;
}

// Shared general client tags — used across every industry. Users can add their own
// custom tags from the New Client modal; those are stored per-client in clients.tags.
export const GENERAL_CLIENT_TAGS: string[] = ['Residential', 'Commercial', 'VIP', 'Recurring', 'Referral', 'Repeat', 'New'];

/** Lo que se elige cuando nada encaja. Va SIEMPRE al final y FUERA de los
 *  grupos, para no tener que buscarlo dentro de uno. */
export const OTHER_SERVICE_INTEREST = 'Other';

/* ── Qué trabajo le interesa al prospecto ────────────────────────────────────
 *
 * Cada oficio traía CINCO opciones y un 'Other'. Con eso, en jardinería, un
 * arreglo de drenaje, una poda, un muro de contención y una instalación de
 * luces exteriores acababan los cuatro apuntados como 'Other', que es lo mismo
 * que no apuntar nada: el campo alimenta el panel del prospecto, el resumen que
 * lee la IA y lo que se ve de un vistazo en la tarjeta del tablero.
 *
 * Las listas salen del corpus de categorías de cada oficio
 * (`documents/categories/<oficio>/`), que es de donde salen también las
 * plantillas de partidas. Se hace así a propósito: lo que se puede APUNTAR como
 * interés es entonces lo mismo que luego se puede PRESUPUESTAR, en vez de dos
 * vocabularios que no se conocen entre sí.
 *
 * LAS OPCIONES QUE YA EXISTÍAN SE CONSERVAN TAL CUAL —'Clean Up', 'New Install',
 * 'Move In/Out', 'Full Replacement'…— y no es cuestión de gusto:
 * `leads.service_interest` guarda LA CADENA, no un id. Renombrar una opción
 * deja a todos los prospectos que ya la tenían con un valor que el desplegable
 * no reconoce, y el `<select>` de un valor desconocido se enseña vacío: el
 * siguiente guardado del panel se lleva por delante el dato sin avisar.
 *
 * Los textos van en inglés y sin pasar por `t()`, igual que `pricebookCategories`
 * y `clientTags` de este mismo archivo. No es un descuido heredado: estas
 * cadenas SE GUARDAN en la base, así que traducirlas partiría cada valor en dos
 * según el idioma en que se apuntó el prospecto. */
const SERVICE_INTEREST_GROUPS: Record<IndustryType, ServiceInterestGroup[]> = {
  landscaping: [
    { label: 'Recurring work', options: ['Maintenance', 'Lawn Care', 'Clean Up', 'Seasonal Color', 'Snow & Ice Management'] },
    { label: 'Design & planting', options: ['Design', 'Installation', 'Planting & Softscape', 'Tree Care', 'Mulch & Beds', 'Sod & Seeding', 'Plant Health & Pest Control'] },
    { label: 'Hardscape & structures', options: ['Hardscape', 'Concrete', 'Retaining Walls', 'Outdoor Structures', 'Water & Fire Features'] },
    { label: 'Systems & site work', options: ['Irrigation', 'Drainage & Grading', 'Outdoor Lighting', 'Site Prep & Demolition'] },
  ],
  general_contractor: [
    { label: 'Projects', options: ['New Build', 'Remodel', 'Addition', 'Basement Finish', 'Garage Conversion', 'ADU / Guest House'] },
    { label: 'Rooms', options: ['Kitchen Remodel', 'Bathroom Remodel', 'Whole-Home Renovation'] },
    { label: 'Structure & exterior', options: ['Framing', 'Foundation Work', 'Roofing', 'Siding & Windows', 'Decks & Porches'] },
    { label: 'Service', options: ['Repair', 'Consultation', 'Punch List', 'Insurance / Damage Repair'] },
  ],
  concrete: [
    { label: 'Flatwork', options: ['Driveway', 'Patio', 'Sidewalk', 'Slab & Flatwork', 'Steps & Stoops', 'Curbs & Edging'] },
    { label: 'Structural', options: ['Foundation', 'Footings', 'Retaining Wall'] },
    { label: 'Decorative', options: ['Stamped & Decorative', 'Staining & Polishing', 'Overlay & Resurfacing'] },
    { label: 'Repair & service', options: ['Repair', 'Slab Leveling & Lifting', 'Sealing & Maintenance', 'Demolition & Removal', 'Consultation'] },
  ],
  electrical: [
    { label: 'Service & power', options: ['New Install', 'Upgrade', 'Panel Upgrade', 'Wiring & Rewiring', 'Appliance Circuit'] },
    { label: 'Lighting & devices', options: ['Interior Lighting', 'Exterior & Landscape Lighting', 'Outlets & Switches', 'Ceiling Fans'] },
    { label: 'Systems', options: ['EV Charger', 'Generator & Backup Power', 'Solar & Battery Storage', 'Smart Home & Low Voltage', 'Surge Protection & Grounding'] },
    { label: 'Service calls', options: ['Repair', 'Inspection', 'Emergency', 'Safety & Code Correction', 'Commercial'] },
  ],
  plumbing: [
    { label: 'Water & fixtures', options: ['New Install', 'Water Heater', 'Fixtures & Faucets', 'Repiping & Water Supply', 'Water Treatment & Filtration'] },
    { label: 'Drains & sewer', options: ['Drain Cleaning', 'Sewer Line', 'Sump Pump & Drainage', 'Backflow Prevention'] },
    { label: 'Projects', options: ['Remodel', 'Rough-In', 'Gas Line', 'Outdoor & Site Plumbing', 'Well & Pump System'] },
    { label: 'Service', options: ['Repair', 'Leak Detection', 'Emergency', 'Inspection', 'Maintenance Plan', 'Commercial'] },
  ],
  painting: [
    { label: 'Painting', options: ['Interior', 'Exterior', 'Trim, Doors & Detail', 'Wall Coverings'] },
    { label: 'Wood & cabinets', options: ['Cabinet Refinish', 'Staining', 'Deck & Fence Staining'] },
    { label: 'Coatings', options: ['Epoxy & Floor Coating', 'Specialty & Decorative Finishes', 'Industrial & Protective Coatings'] },
    { label: 'Prep & repair', options: ['Drywall Repair & Prep', 'Pressure Washing', 'Surface Prep & Remediation', 'Maintenance & Touch-Up'] },
    { label: 'Scale', options: ['Commercial', 'Multi-Unit', 'Consultation'] },
  ],
  cleaning: [
    { label: 'Recurring', options: ['Regular Cleaning', 'Vacation Rental Turnover', 'Commercial', 'Janitorial / Office', 'Medical & Healthcare'] },
    { label: 'One-time', options: ['Deep Clean', 'Move In/Out', 'Post-Construction', 'Specialty & Restoration'] },
    { label: 'Surfaces', options: ['Carpet & Upholstery', 'Hard Floor Care', 'Window & Glass', 'Pressure Washing'] },
    { label: 'Building services', options: ['Gutter Cleaning', 'Air Duct & Vent', 'Disinfection & Sanitizing'] },
  ],
  hvac: [
    { label: 'Cooling', options: ['Central Air Conditioning', 'Heat Pump', 'Ductless Mini-Split'] },
    { label: 'Heating', options: ['Furnace', 'Boiler & Hydronic', 'Water Heating'] },
    { label: 'Air & controls', options: ['Ductwork', 'Ventilation', 'Indoor Air Quality', 'Thermostat & Zoning'] },
    { label: 'Service', options: ['Install', 'Repair', 'Maintenance', 'Inspection', 'Emergency', 'Refrigerant Service', 'Commercial & Rooftop'] },
  ],
  roofing: [
    { label: 'Roof systems', options: ['Full Replacement', 'Asphalt Shingle', 'Metal Roofing', 'Tile Roofing', 'Low-Slope / Flat', 'Wood Shake & Slate'] },
    { label: 'Repair & leaks', options: ['Repair', 'Leak Stop', 'Flashing & Penetrations', 'Decking & Underlayment'] },
    { label: 'Add-ons', options: ['Gutters & Downspouts', 'Roof Ventilation', 'Skylights', 'Solar & Specialty'] },
    { label: 'Service', options: ['Inspection', 'Storm Damage', 'Insurance Claim', 'Maintenance & Cleaning', 'Commercial'] },
  ],
  custom: [
    { label: 'Work', options: ['Installation', 'Service', 'Repair', 'Maintenance', 'Inspection'] },
    { label: 'Projects', options: ['New Project', 'Remodel / Upgrade', 'Consultation', 'Emergency'] },
  ],
};

const flattenServiceInterests = (groups: ServiceInterestGroup[]): string[] =>
  [...groups.flatMap(g => g.options), OTHER_SERVICE_INTEREST];

export const INDUSTRY_CONFIGS: Record<IndustryType, IndustryConfig> = {
  landscaping: {
    label: 'Landscaping',
    icon: 'TreePine',
    pricebookCategories: ['Softscape', 'Hardscape', 'Irrigation', 'Lighting', 'Drainage', 'Chemicals', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.landscaping,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.landscaping),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 1-Year Workmanship',
    defaultWarrantyText: 'We guarantee workmanship for 1 year from the date of completion. Plant material is guaranteed for 30 days unless an automatic irrigation system is installed.',
    scopePromptRole: 'You are an elite Landscaping Project Engineer and a Master Agronomist. Your prime directive is to process rough field notes provided by a landscaping contractor and autonomously synthesize them into a flawless, professional, and visually appealing Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in landscaping, hardscaping, and lawn care contracting',
    warrantyPromptRole: 'warranty specialist for a professional landscaping and hardscaping company',
    chatSystemPrompt: 'You are a helpful business assistant for a landscaping company using a CRM called Raza Pro. Help with estimating, client management, scheduling, and growing a landscaping business.',
    setupPlaceholder: 'e.g. Green Valley Landscaping',
    proposalLabel: 'Landscaping Proposal',
    defaultDescription: 'We are a full-service landscaping company dedicated to creating beautiful outdoor spaces.',
    defaultUpsells: [
      { id: 'u1', name: 'Premium Mulch Upgrade', price: 150, description: 'Upgrade to triple-shredded dyed black mulch.' },
      { id: 'u2', name: 'Seasonal Color Package', price: 225, description: 'Install 2 flats of seasonal annuals near entrance.' }
    ],
    showSiteConditions: true,
  },
  general_contractor: {
    label: 'General Contractor',
    icon: 'HardHat',
    pricebookCategories: ['Framing', 'Concrete', 'Electrical', 'Plumbing', 'Finishing', 'Drywall', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.general_contractor,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.general_contractor),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 1-Year Workmanship',
    defaultWarrantyText: 'We guarantee all workmanship for 1 year from the date of project completion. This warranty covers labor defects only; manufacturer warranties apply to materials separately.',
    scopePromptRole: 'You are a professional General Contractor project manager and estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in general contracting and construction',
    warrantyPromptRole: 'warranty specialist for a professional construction and contracting company',
    chatSystemPrompt: 'You are a helpful business assistant for a general contracting company using a CRM called Raza Pro. Help with estimating, project management, scheduling, and growing a construction business.',
    setupPlaceholder: 'e.g. Summit Builders LLC',
    proposalLabel: 'Project Proposal',
    defaultDescription: 'We are a full-service contracting company committed to quality craftsmanship.',
    defaultUpsells: [
      { id: 'u1', name: 'Extended Warranty', price: 500, description: 'Extend workmanship warranty to 3 years.' },
      { id: 'u2', name: 'Project Photography', price: 200, description: 'Professional before/after documentation.' }
    ],
    showSiteConditions: true,
  },
  concrete: {
    label: 'Concrete',
    icon: 'Box',
    pricebookCategories: ['Flatwork', 'Foundation', 'Curbing', 'Decorative', 'Reinforcement', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.concrete,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.concrete),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 1-Year Workmanship',
    defaultWarrantyText: 'We guarantee workmanship for 1 year from the date of completion. Concrete cracking due to natural settling or freeze-thaw cycles is not covered.',
    scopePromptRole: 'You are a professional concrete contractor and estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in concrete contracting',
    warrantyPromptRole: 'warranty specialist for a professional concrete company',
    chatSystemPrompt: 'You are a helpful business assistant for a concrete contracting company using a CRM called Raza Pro. Help with estimating, scheduling, and growing a concrete business.',
    setupPlaceholder: 'e.g. Solid Ground Concrete',
    proposalLabel: 'Concrete Proposal',
    defaultDescription: 'We are a professional concrete company delivering quality flatwork, foundations, and decorative concrete.',
    defaultUpsells: [
      { id: 'u1', name: 'Stamped Finish Upgrade', price: 400, description: 'Upgrade to stamped pattern finish.' },
      { id: 'u2', name: 'Sealer Application', price: 200, description: 'Apply protective concrete sealer.' }
    ],
    showSiteConditions: true,
  },
  electrical: {
    label: 'Electrical',
    icon: 'Zap',
    pricebookCategories: ['Wiring', 'Fixtures', 'Panels', 'Conduit', 'Controls', 'Safety', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.electrical,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.electrical),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 1-Year Workmanship',
    defaultWarrantyText: 'We guarantee all workmanship for 1 year from the date of completion. Electrical components carry their own manufacturer warranty.',
    scopePromptRole: 'You are a licensed electrician and electrical project estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in electrical contracting',
    warrantyPromptRole: 'warranty specialist for a professional electrical company',
    chatSystemPrompt: 'You are a helpful business assistant for an electrical contracting company using a CRM called Raza Pro. Help with estimating, scheduling, and growing an electrical business.',
    setupPlaceholder: 'e.g. Bolt Electric LLC',
    proposalLabel: 'Electrical Proposal',
    defaultDescription: 'We are a licensed electrical company providing safe, reliable installations and repairs.',
    defaultUpsells: [
      { id: 'u1', name: 'Surge Protection', price: 300, description: 'Whole-home surge protection system.' },
      { id: 'u2', name: 'Smart Lighting Package', price: 350, description: 'Smart switch and dimmer upgrade.' }
    ],
    showSiteConditions: false,
  },
  plumbing: {
    label: 'Plumbing',
    icon: 'Wrench',
    pricebookCategories: ['Pipes', 'Fixtures', 'Valves', 'Drains', 'Water Heaters', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.plumbing,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.plumbing),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 1-Year Workmanship',
    defaultWarrantyText: 'We guarantee all workmanship for 1 year from the date of completion. Plumbing fixtures and components carry their own manufacturer warranty.',
    scopePromptRole: 'You are a licensed plumber and plumbing project estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in plumbing contracting',
    warrantyPromptRole: 'warranty specialist for a professional plumbing company',
    chatSystemPrompt: 'You are a helpful business assistant for a plumbing company using a CRM called Raza Pro. Help with estimating, scheduling, and growing a plumbing business.',
    setupPlaceholder: 'e.g. FlowMaster Plumbing',
    proposalLabel: 'Plumbing Proposal',
    defaultDescription: 'We are a licensed plumbing company providing expert installations and repairs.',
    defaultUpsells: [
      { id: 'u1', name: 'Water Filtration System', price: 450, description: 'Whole-house water filtration.' },
      { id: 'u2', name: 'Tankless Water Heater Upgrade', price: 1200, description: 'Energy-efficient tankless system.' }
    ],
    showSiteConditions: false,
  },
  painting: {
    label: 'Painting',
    icon: 'Paintbrush',
    pricebookCategories: ['Interior Paint', 'Exterior Paint', 'Prep Materials', 'Stains', 'Equipment', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.painting,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.painting),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 2-Year Workmanship',
    defaultWarrantyText: 'We guarantee all workmanship for 2 years from the date of completion. This includes peeling, bubbling, or flaking caused by improper application.',
    scopePromptRole: 'You are a professional painting contractor and estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in painting and coating contracting',
    warrantyPromptRole: 'warranty specialist for a professional painting company',
    chatSystemPrompt: 'You are a helpful business assistant for a painting company using a CRM called Raza Pro. Help with estimating, scheduling, and growing a painting business.',
    setupPlaceholder: 'e.g. PrimeLine Painting',
    proposalLabel: 'Painting Proposal',
    defaultDescription: 'We are a professional painting company delivering beautiful, lasting finishes.',
    defaultUpsells: [
      { id: 'u1', name: 'Premium Paint Upgrade', price: 300, description: 'Upgrade to Sherwin-Williams SuperPaint.' },
      { id: 'u2', name: 'Accent Wall Package', price: 200, description: 'One feature accent wall with designer color.' }
    ],
    showSiteConditions: false,
  },
  cleaning: {
    label: 'Cleaning',
    icon: 'Sparkles',
    pricebookCategories: ['Supplies', 'Equipment', 'Chemicals', 'Specialty', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.cleaning,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.cleaning),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Satisfaction Guarantee',
    defaultWarrantyText: 'We guarantee your satisfaction. If you are not happy with our work, we will return within 48 hours to address any concerns at no additional charge.',
    scopePromptRole: 'You are a professional cleaning service manager and estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in cleaning and janitorial services',
    warrantyPromptRole: 'satisfaction guarantee specialist for a professional cleaning company',
    chatSystemPrompt: 'You are a helpful business assistant for a cleaning services company using a CRM called Raza Pro. Help with estimating, scheduling, and growing a cleaning business.',
    setupPlaceholder: 'e.g. SparkClean Services',
    proposalLabel: 'Cleaning Proposal',
    defaultDescription: 'We are a professional cleaning company dedicated to spotless results every time.',
    defaultUpsells: [
      { id: 'u1', name: 'Carpet Deep Clean', price: 150, description: 'Professional carpet extraction cleaning.' },
      { id: 'u2', name: 'Window Cleaning Package', price: 100, description: 'Interior and exterior window cleaning.' }
    ],
    showSiteConditions: false,
  },
  hvac: {
    label: 'HVAC',
    icon: 'Thermometer',
    pricebookCategories: ['Units', 'Ductwork', 'Controls', 'Refrigerant', 'Filters', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.hvac,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.hvac),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 1-Year Workmanship',
    defaultWarrantyText: 'We guarantee all workmanship for 1 year from the date of completion. HVAC units and components carry their own manufacturer warranty.',
    scopePromptRole: 'You are a licensed HVAC technician and project estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in HVAC contracting',
    warrantyPromptRole: 'warranty specialist for a professional HVAC company',
    chatSystemPrompt: 'You are a helpful business assistant for an HVAC company using a CRM called Raza Pro. Help with estimating, scheduling, and growing an HVAC business.',
    setupPlaceholder: 'e.g. CoolBreeze HVAC',
    proposalLabel: 'HVAC Proposal',
    defaultDescription: 'We are a licensed HVAC company providing expert climate control solutions.',
    defaultUpsells: [
      { id: 'u1', name: 'Smart Thermostat', price: 350, description: 'Ecobee or Nest smart thermostat install.' },
      { id: 'u2', name: 'Annual Maintenance Plan', price: 250, description: 'Bi-annual tune-up and priority service.' }
    ],
    showSiteConditions: false,
  },
  roofing: {
    label: 'Roofing',
    icon: 'Home',
    pricebookCategories: ['Shingles', 'Underlayment', 'Flashing', 'Gutters', 'Ventilation', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.roofing,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.roofing),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 5-Year Workmanship',
    defaultWarrantyText: 'We guarantee all workmanship for 5 years from the date of completion. Roofing materials carry their own manufacturer warranty, often 20-50 years.',
    scopePromptRole: 'You are a professional roofing contractor and estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in roofing and exterior contracting',
    warrantyPromptRole: 'warranty specialist for a professional roofing company',
    chatSystemPrompt: 'You are a helpful business assistant for a roofing company using a CRM called Raza Pro. Help with estimating, scheduling, and growing a roofing business.',
    setupPlaceholder: 'e.g. Apex Roofing Solutions',
    proposalLabel: 'Roofing Proposal',
    defaultDescription: 'We are a professional roofing company committed to protecting your home.',
    defaultUpsells: [
      { id: 'u1', name: 'Ridge Vent Upgrade', price: 400, description: 'Continuous ridge ventilation system.' },
      { id: 'u2', name: 'Gutter Guard Package', price: 600, description: 'Full gutter guard installation.' }
    ],
    showSiteConditions: true,
  },
  custom: {
    label: 'Other / Custom',
    icon: 'Briefcase',
    pricebookCategories: ['Materials', 'Equipment', 'Labor', 'Subcontractor', 'Other'],
    serviceInterestGroups: SERVICE_INTEREST_GROUPS.custom,
    serviceInterests: flattenServiceInterests(SERVICE_INTEREST_GROUPS.custom),
    clientTags: GENERAL_CLIENT_TAGS,
    defaultWarrantyName: 'Standard 1-Year Workmanship',
    defaultWarrantyText: 'We guarantee all workmanship for 1 year from the date of completion.',
    scopePromptRole: 'You are a professional project manager and estimator. Your prime directive is to process rough field notes and synthesize them into a flawless, professional Scope of Work (SOW) ready for client presentation.',
    legalPromptRole: 'legal advisor specializing in service contracting',
    warrantyPromptRole: 'warranty specialist for a professional service company',
    chatSystemPrompt: 'You are a helpful business assistant for a service company using a CRM called Raza Pro. Help with estimating, scheduling, and growing the business.',
    setupPlaceholder: 'Your Business Name',
    proposalLabel: 'Proposal',
    defaultDescription: 'We are a professional service company committed to quality and customer satisfaction.',
    defaultUpsells: [],
    showSiteConditions: false,
  },
};

/** Get the industry config for a given type, with safe fallback to landscaping */
export const getIndustryConfig = (type?: string | null): IndustryConfig => {
  if (!type) return INDUSTRY_CONFIGS.landscaping;
  return INDUSTRY_CONFIGS[type as IndustryType] || INDUSTRY_CONFIGS.landscaping;
};

/** Get all industry options for dropdowns */
export const INDUSTRY_OPTIONS = Object.entries(INDUSTRY_CONFIGS).map(([key, cfg]) => ({
  value: key,
  label: cfg.label,
}));
